import React, { useState, useEffect } from "react";
import { useFetchCenturyAuditTrailMutation } from "../../../features/tenant/Century/centuryApi";
import { useFetchStateListV2Mutation } from "../../../features/tenant/pincode/pincodeApi";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import MultiSelectComponent from "../../../components/Tenant/MultiSelectComponent/MultiSelectComponent";
import toast from "react-hot-toast";
import { HiOutlineDownload } from "react-icons/hi";
import { States } from "../../../utils/getStates";
import dayjs from "dayjs";

const CenturyAuditTrail = () => {
  const [activeTab, setActiveTab] = useState("mypoint"); // "mypoint" or "arjunpoint"

  // State options
  const [stateOptions, setStateOptions] = useState([
    { name: "All States", id: "all" },
  ]);
  const [selectedStates, setSelectedStates] = useState([stateOptions[0]]);

  // Sync "All States" vs other states
  useEffect(() => {
    if (selectedStates.length > 1) {
      const hasAll = selectedStates.some((st) => st.id === "all");
      const lastSelected = selectedStates[selectedStates.length - 1];

      if (lastSelected.id === "all") {
        setSelectedStates([{ name: "All States", id: "all" }]);
      } else if (hasAll) {
        setSelectedStates(selectedStates.filter((st) => st.id !== "all"));
      }
    } else if (selectedStates.length === 0) {
      setSelectedStates([{ name: "All States", id: "all" }]);
    }
  }, [selectedStates]);

  // User type options
  const [userTypes, setUserTypes] = useState([
    { name: "Stockist", user_type: "stockist" },
    { name: "Retailer", user_type: "retailer" },
  ]);
  const [selectedUserType, setSelectedUserType] = useState(userTypes[0]);

  const [ledgerData, setLedgerData] = useState([]);
  const [expiryDate, setExpiryDate] = useState("");

  const [fetchAuditTrail, { isLoading }] = useFetchCenturyAuditTrailMutation();
  const [fetchStateList] = useFetchStateListV2Mutation();

  useEffect(() => {
    const getStates = async () => {
      try {
        const response = await fetchStateList({
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        }).unwrap();
        if (response.success && response.body?.states) {
          const mapped = response.body.states.map((st) => ({
            name: st,
            id: st,
          }));
          setStateOptions([{ name: "All States", id: "all" }, ...mapped]);
        }
      } catch (error) {
        console.error("Error fetching states", error);
        // Fallback to static States list
        const fallback = States.map((s) => ({ name: s.name, id: s.name }));
        setStateOptions([{ name: "All States", id: "all" }, ...fallback]);
      }
    };
    getStates();

    const userTypesFromSession = sessionStorage.getItem("appUserType") || sessionStorage.getItem("userTypes");
    if (userTypesFromSession) {
      try {
        const parsed = JSON.parse(userTypesFromSession);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const mapped = parsed.map((t) => {
            const rawType = t.user_type || t.name;
            const displayName = rawType.charAt(0).toUpperCase() + rawType.slice(1);
            return {
              name: displayName,
              user_type: rawType,
            };
          });
          setUserTypes(mapped);
          setSelectedUserType(mapped[0]);
        }
      } catch (e) {
        console.error("Error parsing userTypes", e);
      }
    }
  }, []);

  const handleFetch = async (tabToFetch = activeTab) => {
    if (selectedStates.length === 0 || !selectedUserType) {
      toast.error("Please select all filters");
      return;
    }

    try {
      const statePayload = selectedStates.some((st) => st.id === "all")
        ? ["all"]
        : selectedStates.map((st) => st.id);

      const payload = {
        slug: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        token: sessionStorage.getItem("access_token"),
        body: {
          pointType: tabToFetch,
          state: statePayload,
          userType: selectedUserType.user_type,
        },
      };

      const response = await fetchAuditTrail(payload).unwrap();
      if (response.success && response.body) {
        setLedgerData(response.body.ledger || []);
        setExpiryDate(response.body.expiryDate || "");
        toast.success(response.message || "Audit trail data fetched successfully");
      } else {
        setLedgerData([]);
        setExpiryDate("");
        toast.error(response.message || "No data found");
      }
    } catch (error) {
      console.error(error);
      setLedgerData([]);
      setExpiryDate("");
      toast.error("Failed to fetch audit trail data");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setLedgerData([]);
    setExpiryDate("");
  };

  const handleDownloadCsv = () => {
    if (ledgerData.length === 0) {
      toast.error("No data available to download");
      return;
    }

    const csvRows = [];
    csvRows.push("Century Audit Trail Report");
    csvRows.push(`Point Type:,${activeTab === "mypoint" ? "My Point" : "Arjun Point"}`);
    csvRows.push(`User Type:,${selectedUserType?.name || "N/A"}`);
    const stateNames = selectedStates.map((st) => st.name).join(" | ");
    csvRows.push(`State:,${stateNames || "N/A"}`);
    csvRows.push(`Report Generation Date:,${dayjs().format("DD/MM/YYYY HH:mm:ss")}`);
   
    if (expiryDate) {
      csvRows.push(`Expiry Cutoff Date:,${dayjs(expiryDate).format("DD/MM/YYYY")}`);
    }
    csvRows.push(""); // Empty spacing line

    let headers = [];
    if (activeTab === "mypoint") {
      headers = [
        "S.No",
        "User ID",
        "User Name",
        "Mobile No",
        "City",
        "State",
        "Total Points",
        "Earned Points (Before Expiry)",
        "Total Redeemed Points",
        "Points to Expire",
      ];
    } else {
      headers = [
        "S.No",
        "User ID",
        "User Name",
        "Mobile No",
        "City",
        "State",
        "Product Code",
        "Purchased Qty",
        "Sold Qty",
        "Expiring Qty",
        "Points Expired",
      ];
    }
    csvRows.push(headers.join(","));

    ledgerData.forEach((row, index) => {
      let rowData = [];
      if (activeTab === "mypoint") {
        rowData = [
          index + 1,
          row.app_user_id || "N/A",
          row.name || "N/A",
          row.mobile || "N/A",
          row.city || "N/A",
          row.state || "N/A",
          row.total_points || 0,
          row.earned_points || 0,
          row.total_redeemed_points || 0,
          row.points_to_expire || 0,
        ];
      } else {
        rowData = [
          index + 1,
          row.app_user_id || "N/A",
          row.name || "N/A",
          row.mobile || "N/A",
          row.city || "N/A",
          row.state || "N/A",
          row.product_code || "N/A",
          row.purchased_qty || 0,
          row.sold_qty || 0,
          row.expiring_qty || 0,
          row.point_expired || 0,
        ];
      }
      csvRows.push(rowData.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Audit_Trail_${activeTab}_${selectedUserType?.name}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const myPointColumns = [
    {
      name: "S.No",
      cell: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "User ID",
      cell: (row) => row.app_user_id || "N/A",
      sortable: true,
      width: "100px",
    },
    {
      name: "User Name",
      cell: (row) => row.name || "N/A",
      sortable: true,
    },
    {
      name: "Mobile",
      cell: (row) => row.mobile || "N/A",
      sortable: true,
    },
    {
      name: "City",
      cell: (row) => row.city || "N/A",
      sortable: true,
    },
    {
      name: "State",
      cell: (row) => row.state || "N/A",
      sortable: true,
    },
    {
      name: "Total Points",
      cell: (row) => <span className="font-semibold text-gray-800">{row.total_points || 0}</span>,
      sortable: true,
    },
    {
      name: "Earned Points",
      cell: (row) => <span className="font-semibold text-blue-600">{row.earned_points || 0}</span>,
      sortable: true,
    },
    {
      name: "Total Redeemed",
      cell: (row) => <span className="font-semibold text-purple-600">{row.total_redeemed_points || 0}</span>,
      sortable: true,
    },
    {
      name: "Points to Expire",
      cell: (row) => <span className="font-bold text-red-600">{row.points_to_expire || 0}</span>,
      sortable: true,
    },
  ];

  const arjunPointColumns = [
    {
      name: "S.No",
      cell: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "User ID",
      cell: (row) => row.app_user_id || "N/A",
      sortable: true,
      width: "100px",
    },
    {
      name: "User Name",
      cell: (row) => row.name || "N/A",
      sortable: true,
    },
    {
      name: "Mobile",
      cell: (row) => row.mobile || "N/A",
      sortable: true,
    },
    {
      name: "City",
      cell: (row) => row.city || "N/A",
      sortable: true,
    },
    {
      name: "State",
      cell: (row) => row.state || "N/A",
      sortable: true,
    },
    {
      name: "Product Code",
      cell: (row) => row.product_code || "N/A",
      sortable: true,
    },
    {
      name: "Purchased Qty",
      cell: (row) => <span className="font-semibold text-blue-600">{row.purchased_qty || 0}</span>,
      sortable: true,
    },
    {
      name: "Sold Qty",
      cell: (row) => <span className="font-semibold text-indigo-600">{row.sold_qty || 0}</span>,
      sortable: true,
    },
    {
      name: "Expiring Qty",
      cell: (row) => <span className="font-bold text-amber-600">{row.expiring_qty || 0}</span>,
      sortable: true,
    },
    {
      name: "Points Expired",
      cell: (row) => <span className="font-bold text-red-600">{row.point_expired || 0}</span>,
      sortable: true,
    },
  ];

  return (
    <div className="flex w-full bg-gray-100 h-full justify-center">
      <div className="w-full">
        <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold flex justify-between items-center pr-6">
          Century Audit Trail
          <LinksComponent />
        </div>

        <div className="p-4">
          <div className="bg-white rounded-xl shadow-lg transition-all duration-300">
            <div className="flex border-b overflow-hidden rounded-t-xl">
              <button
                onClick={() => handleTabChange("mypoint")}
                className={`flex-1 py-4 text-center font-bold text-sm uppercase tracking-wider transition-all duration-200 rounded-tl-xl ${
                  activeTab === "mypoint"
                    ? "bg-blue-600 text-white shadow-inner"
                    : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                }`}
              >
                My Point Audit
              </button>
              <button
                onClick={() => handleTabChange("arjunpoint")}
                className={`flex-1 py-4 text-center font-bold text-sm uppercase tracking-wider transition-all duration-200 rounded-tr-xl ${
                  activeTab === "arjunpoint"
                    ? "bg-blue-600 text-white shadow-inner"
                    : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                }`}
              >
                Arjun Point Audit
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">State</label>
                    <MultiSelectComponent
                      required={false}
                      selected={selectedStates}
                      setSelected={setSelectedStates}
                      dataArr={stateOptions}
                      placeholder="Select State(s)..."
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">User Type</label>
                    <SelectComponent
                      selected={selectedUserType}
                      setSelected={setSelectedUserType}
                      dataArr={userTypes}
                    />
                  </div>

                  <button
                    onClick={() => handleFetch(activeTab)}
                    disabled={isLoading}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Fetching..." : "Fetch Audit Trail"}
                  </button>

                  {ledgerData.length > 0 && (
                    <button
                      onClick={handleDownloadCsv}
                      className="flex items-center justify-center gap-2 w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95"
                    >
                      <HiOutlineDownload size={20} />
                      Export Data
                    </button>
                  )}
                </div>

                {expiryDate && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex justify-between items-center text-blue-700">
                    <span className="font-semibold">Expiry Date Cutoff:</span>
                    <span className="font-bold bg-blue-100 px-3 py-1 rounded-full text-sm">
                      {expiryDate}
                    </span>
                  </div>
                )}

                <div className="rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                  <DataTableComponent
                    columns={activeTab === "mypoint" ? myPointColumns : arjunPointColumns}
                    data={ledgerData}
                    pagination={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CenturyAuditTrail;
